/**
 * Phase 6 Tier 1 — Sentry Triage Panel (Feb 2026)
 *
 * Embedded inside Settings → super-admin only. One row per Sentry alert with
 * the classification badge, Claude-generated root cause, the proposed fix
 * (if any) as collapsible diff, and lifecycle controls.
 *
 * Goal: when an alert email lands, opening this panel is enough to know what
 * happened and approve/dismiss without ever pasting anything into the agent.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { RefreshCw, ChevronDown, ChevronRight, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const CLASS_STYLES = {
  NOISE:        { color: '#6B8E73', label: 'NOISE',        hint: '3rd-party / browser noise' },
  KNOWN_ISSUE:  { color: '#00F0FF', label: 'KNOWN',        hint: 'Recognized benign pattern' },
  NEEDS_HUMAN:  { color: '#FF9F0A', label: 'NEEDS_HUMAN',  hint: 'Real bug — no auto-fix' },
  FIX_READY:    { color: '#39FF14', label: 'FIX_READY',    hint: 'Patch proposed below' },
  FATAL:        { color: '#FF3B30', label: 'FATAL',        hint: 'Auth/orders/payments — escalated' },
};

const STATUS_STYLES = {
  open:               { color: '#39FF14', label: 'OPEN' },
  approved_for_fix:   { color: '#00F0FF', label: 'APPROVED' },
  reviewed:           { color: '#A3C7A8', label: 'REVIEWED' },
  dismissed:          { color: '#6B8E73', label: 'DISMISSED' },
};

export function SentryTriagePanel() {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_classification: {}, by_status: {} });
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(''); // empty = all

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const { data } = await axios.get(`${API_URL}/api/admin/sentry-triage/alerts${params}`, { withCredentials: true });
      setAlerts(data.alerts || []);
      setStats(data.stats || { total: 0, by_classification: {}, by_status: {} });
    } catch (e) {
      toast.error(`Failed to load Sentry alerts: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const approveFix = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/sentry-triage/alerts/${id}/approve-fix`, { note: '' }, { withCredentials: true });
      toast.success('Fix queued — pick up on next agent run');
      fetchAlerts();
    } catch (e) {
      toast.error(`Approve failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const dismiss = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/sentry-triage/alerts/${id}/dismiss`, { note: '' }, { withCredentials: true });
      toast.success('Alert dismissed');
      fetchAlerts();
    } catch (e) {
      toast.error(`Dismiss failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const reanalyze = async (id) => {
    try {
      toast.message('Re-running Claude analysis…');
      await axios.post(`${API_URL}/api/admin/sentry-triage/alerts/${id}/reanalyze`, {}, { withCredentials: true });
      toast.success('Re-analysis complete');
      fetchAlerts();
    } catch (e) {
      toast.error(`Reanalyze failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Feb 2026 — bulk sweep: run the cheap noise filter against every `open`
  // alert and auto-dismiss the ones that match known browser-extension /
  // environmental patterns. Useful right after we extend the noise list.
  const sweepNoise = async () => {
    try {
      toast.message('Sweeping open alerts for noise…');
      const { data } = await axios.post(
        `${API_URL}/api/admin/sentry-triage/reclassify-noise`,
        {},
        { withCredentials: true },
      );
      const n = data?.reclassified || 0;
      const scanned = data?.scanned || 0;
      if (n === 0) {
        toast.success(`Scanned ${scanned} open alert${scanned === 1 ? '' : 's'} — no noise matches`);
      } else {
        toast.success(`Dismissed ${n} noise alert${n === 1 ? '' : 's'} of ${scanned} scanned`);
      }
      fetchAlerts();
    } catch (e) {
      toast.error(`Sweep failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div id="sentry-triage" className="bg-[#0C0612] border border-[#FF3B30]/60 mb-6 scroll-mt-24" data-testid="sentry-triage-panel">
      <div className="p-4 border-b border-[#FF3B30]/40 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-[#FF3B30] font-mono text-sm uppercase font-bold flex items-center gap-2">
            <Sparkles className="w-4 h-4" />// Sentry Triage Agent
          </h3>
          <p className="text-[#6B8E73] font-mono text-[10px] mt-1">
            Auto-analysis of every Sentry alert. Emails keep flowing — this panel just adds the &quot;already analyzed&quot; layer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={sweepNoise}
            disabled={loading}
            variant="outline"
            className="border-[#6B8E73] text-[#6B8E73] hover:bg-[#6B8E73]/10 font-mono text-xs rounded-none"
            data-testid="sentry-triage-sweep-noise-btn"
            title="Re-run the noise heuristic against every open alert. Dismisses browser-extension / environmental noise in bulk."
          >
            <XCircle className="w-3 h-3 mr-1" />
            SWEEP NOISE
          </Button>
          <Button
            onClick={fetchAlerts}
            disabled={loading}
            variant="outline"
            className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 font-mono text-xs rounded-none"
            data-testid="sentry-triage-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            REFRESH
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-3 border-b border-[#FF3B30]/20" data-testid="sentry-triage-stats">
        {Object.entries(CLASS_STYLES).map(([cls, meta]) => (
          <div key={cls} className="bg-[#050003] border border-[#2A0F3D] p-2 text-center">
            <div className="font-mono text-[10px] uppercase" style={{ color: meta.color }}>{meta.label}</div>
            <div className="font-mono text-lg font-bold mt-1" style={{ color: meta.color }}>
              {stats.by_classification?.[cls] || 0}
            </div>
          </div>
        ))}
      </div>

      {/* Status filter chips */}
      <div className="flex gap-2 p-3 flex-wrap border-b border-[#FF3B30]/20">
        {['', 'open', 'approved_for_fix', 'reviewed', 'dismissed'].map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            className={`font-mono text-[10px] uppercase px-2 py-1 border rounded-none ${
              statusFilter === s
                ? 'border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10'
                : 'border-[#2A0F3D] text-[#6B8E73] hover:border-[#39FF14]/40'
            }`}
            data-testid={`sentry-triage-filter-${s || 'all'}`}
          >
            {s || 'ALL'} {s && (stats.by_status?.[s] != null ? `(${stats.by_status[s]})` : '')}
          </button>
        ))}
      </div>

      {/* Alert list */}
      <div className="divide-y divide-[#2A0F3D]" data-testid="sentry-triage-list">
        {loading && alerts.length === 0 && (
          <p className="p-4 text-[#6B8E73] font-mono text-xs">// Loading…</p>
        )}
        {!loading && alerts.length === 0 && (
          <p className="p-4 text-[#4B6B52] font-mono text-xs" data-testid="sentry-triage-empty">
            // No alerts yet. When Sentry fires a webhook to /api/sentry/webhook, it will appear here within seconds.
          </p>
        )}
        {alerts.map((a) => {
          const cls = CLASS_STYLES[a.classification] || { color: '#A3C7A8', label: a.classification || '—', hint: '' };
          const st = STATUS_STYLES[a.status] || { color: '#A3C7A8', label: (a.status || '—').toUpperCase() };
          const isOpen = expandedId === a.id;
          return (
            <div key={a.id} className="p-3" data-testid={`sentry-alert-${a.id}`}>
              <button
                onClick={() => setExpandedId(isOpen ? null : a.id)}
                className="w-full text-left flex items-start gap-2"
                data-testid={`sentry-alert-toggle-${a.id}`}
              >
                {isOpen
                  ? <ChevronDown className="w-4 h-4 mt-1 text-[#6B8E73] shrink-0" />
                  : <ChevronRight className="w-4 h-4 mt-1 text-[#6B8E73] shrink-0" />
                }
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      className="font-mono text-[10px] uppercase rounded-none border"
                      style={{ color: cls.color, borderColor: cls.color, background: 'transparent' }}
                    >
                      {cls.label}
                    </Badge>
                    <Badge
                      className="font-mono text-[10px] uppercase rounded-none border"
                      style={{ color: st.color, borderColor: st.color, background: 'transparent' }}
                    >
                      {st.label}
                    </Badge>
                    <span className="font-mono text-[10px] text-[#6B8E73]">
                      conf {(a.confidence || 0).toFixed(2)}
                    </span>
                    <span className="font-mono text-[10px] text-[#6B8E73]">
                      {a.received_at ? new Date(a.received_at).toLocaleString() : ''}
                    </span>
                  </div>
                  <p className="text-[#39FF14] font-mono text-xs mt-1 truncate">{a.issue_title || '(no title)'}</p>
                  <p className="text-[#A3C7A8] font-mono text-[11px] mt-1 line-clamp-2">{a.root_cause}</p>
                </div>
              </button>

              {isOpen && (
                <div className="mt-3 ml-6 space-y-3" data-testid={`sentry-alert-expanded-${a.id}`}>
                  {/* Next step */}
                  <div className="bg-[#050003] border border-[#2A0F3D] p-2">
                    <p className="font-mono text-[10px] text-[#6B8E73] uppercase mb-1">// Next step</p>
                    <p className="font-mono text-xs text-[#A3C7A8]">{a.next_step}</p>
                  </div>

                  {/* Proposed fix */}
                  {a.proposed_fix && (
                    <div className="bg-[#050003] border border-[#39FF14]/40 p-2">
                      <p className="font-mono text-[10px] text-[#39FF14] uppercase mb-1">// Proposed fix</p>
                      <p className="font-mono text-xs text-[#A3C7A8]">
                        <strong>{a.proposed_fix.file}</strong> — {a.proposed_fix.summary}
                      </p>
                      {a.proposed_fix.diff && (
                        <pre className="mt-2 p-2 bg-[#000] border border-[#39FF14]/20 font-mono text-[10px] text-[#A3C7A8] overflow-x-auto whitespace-pre-wrap">
                          {a.proposed_fix.diff}
                        </pre>
                      )}
                    </div>
                  )}

                  {/* Meta */}
                  <div className="grid grid-cols-2 gap-2 text-[10px] font-mono text-[#6B8E73]">
                    <div>issue: <span className="text-[#A3C7A8]">{a.issue_short_id || a.issue_id || '—'}</span></div>
                    <div>event: <span className="text-[#A3C7A8]">{a.event_id || '—'}</span></div>
                    <div>level: <span className="text-[#A3C7A8]">{a.level || '—'}</span></div>
                    <div>platform: <span className="text-[#A3C7A8]">{a.platform || '—'}</span></div>
                    {a.culprit && <div className="col-span-2">culprit: <span className="text-[#A3C7A8]">{a.culprit}</span></div>}
                    {a.issue_url && (
                      <div className="col-span-2">
                        <a href={a.issue_url} target="_blank" rel="noreferrer" className="text-[#00F0FF] underline">
                          → Open in Sentry
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Controls */}
                  <div className="flex gap-2 flex-wrap pt-1">
                    {a.proposed_fix && a.status === 'open' && (
                      <Button
                        onClick={() => approveFix(a.id)}
                        className="bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-mono text-xs rounded-none"
                        data-testid={`sentry-alert-approve-${a.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />APPROVE FIX
                      </Button>
                    )}
                    {a.status === 'open' && (
                      <Button
                        onClick={() => dismiss(a.id)}
                        variant="outline"
                        className="border-[#6B8E73] text-[#6B8E73] hover:bg-[#6B8E73]/10 font-mono text-xs rounded-none"
                        data-testid={`sentry-alert-dismiss-${a.id}`}
                      >
                        <XCircle className="w-3 h-3 mr-1" />DISMISS
                      </Button>
                    )}
                    <Button
                      onClick={() => reanalyze(a.id)}
                      variant="outline"
                      className="border-[#BF5AF2] text-[#BF5AF2] hover:bg-[#BF5AF2]/10 font-mono text-xs rounded-none"
                      data-testid={`sentry-alert-reanalyze-${a.id}`}
                    >
                      <Sparkles className="w-3 h-3 mr-1" />RE-ANALYZE
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
